import { vocab } from '@/ds';
import { formatNumber } from '@/lib/format/format';
import { statusFromApi, type OrganizationTab } from '@/features/organizations/vocab';
import type { Organization } from './api';

export const SUPPORT_EMAIL = 'support@runqo.app';

export const NAME_MIN = 2;
export const NAME_MAX = 100;
export const DESCRIPTION_MIN = 10;
export const DESCRIPTION_MAX = 500;

type WithStatus = Pick<Organization, 'status'>;

/** The design system's word for the status: 'pending', 'approved', 'suspended', 'rejected'. */
export const organizationStatus = (organization: WithStatus): OrganizationTab =>
  statusFromApi(organization.status) ?? 'pending';

/**
 * The approval rule, in one place: only an Approved organization manages events. Any other
 * status makes no organization-scoped call, since the API refuses them all.
 */
export const canManageEvents = (organization: WithStatus): boolean =>
  organization.status === 'Approved';

/** Members follow the same rule: the API opens them only for an approved organization. */
export const canManageMembers = canManageEvents;

const BLOCKED: Record<Exclude<OrganizationTab, 'approved'>, string> = {
  pending: 'Events open once Runqo approves this organization.',
  suspended: 'This organization is suspended, so its events can’t be edited.',
  rejected: 'Runqo didn’t approve this organization, so it can’t manage events.',
};

/** Why event actions are off, shown beside each disabled one; undefined while they're on. */
export function eventsBlockedReason(organization: WithStatus): string | undefined {
  const status = organizationStatus(organization);
  return status === 'approved' ? undefined : BLOCKED[status];
}

export interface StatusNotice {
  tone: 'info' | 'critical';
  title: string;
  text: string;
  /** Leads into the support address, when writing to Runqo is the next step. */
  contact?: string;
}

/** What each status means for the organizer and what happens next, for the workspace's banner. */
export const STATUS_NOTICES: Record<Exclude<OrganizationTab, 'approved'>, StatusNotice> = {
  pending: {
    tone: 'info',
    title: 'Waiting for Runqo approval',
    text: 'A Runqo operator reviews each new organization before it can manage events. Nothing else is needed from you: events open here once it’s approved.',
  },
  suspended: {
    tone: 'critical',
    title: 'Suspended by Runqo',
    text: 'Its events stay live in the app, but they can’t be edited until Runqo reactivates it.',
    contact: 'To find out why, write to',
  },
  rejected: {
    tone: 'critical',
    title: 'Not approved by Runqo',
    text: 'It can’t manage events.',
    contact: 'If you think this is a mistake, write to',
  },
};

export const statusNotice = (organization: WithStatus): StatusNotice | undefined => {
  const status = organizationStatus(organization);
  return status === 'approved' ? undefined : STATUS_NOTICES[status];
};

/** An organization's events by date: upcoming from today (Belgrade), past until yesterday. */
export const EVENT_TABS = ['upcoming', 'past'] as const;
export type EventTab = (typeof EVENT_TABS)[number];

export const tabLabel = (tab: EventTab): string =>
  tab === 'upcoming' ? 'Upcoming' : (vocab.STATUS.event?.past?.label ?? 'Past');

/** What the events list says while the organization can't manage events. */
export const EVENTS_LIST_BLOCKED: Record<Exclude<OrganizationTab, 'approved'>, string> = {
  pending: 'Your events are listed here once Runqo approves this organization.',
  suspended:
    'Its events stay live in the app. They’re listed here again once Runqo reactivates it.',
  rejected: 'Events are listed here only for an organization Runqo has approved.',
};

/** What the members page says while the organization isn't approved. */
export const MEMBERS_BLOCKED: Record<Exclude<OrganizationTab, 'approved'>, string> = {
  pending: 'Members open once Runqo approves this organization.',
  suspended: 'While it’s suspended, its members can’t be listed or changed here.',
  rejected: 'Members are managed here only for an organization Runqo has approved.',
};

export function membersBlockedReason(organization: WithStatus): string | undefined {
  const status = organizationStatus(organization);
  return status === 'approved' ? undefined : MEMBERS_BLOCKED[status];
}

export type MemberRole = 'owner' | 'member';
export const roleTerm = (role: MemberRole) => vocab.MEMBER_ROLES[role];

export const isCurator = (organization: Pick<Organization, 'type'>): boolean =>
  organization.type === 'Curator';

/**
 * Why the organization can't be deleted, as the API refuses it: a Curator never, any other only
 * once it has no events. `eventCount` is unknown while it isn't approved (nothing asks then).
 */
export function deleteBlockedReason(
  organization: Pick<Organization, 'type'>,
  eventCount?: number,
): string | undefined {
  if (isCurator(organization))
    return 'A Curator organization holds Runqo’s catalog and can’t be deleted.';
  if (!eventCount) return undefined;
  return eventCount === 1
    ? 'It still has 1 event. Move it to another organization or delete it first.'
    : `It still has ${formatNumber(eventCount)} events. Move them to another organization or delete them first.`;
}

/** What deleting means while the organization isn't approved, beside the general rule. */
export const DELETE_NOTES: Partial<Record<OrganizationTab, string>> = {
  suspended:
    'While it’s suspended, its events can’t be moved or deleted here, so it can be deleted only if it has none.',
};

export type OrganizationKind = 'owner' | 'curator';
export const ORGANIZATION_KINDS: OrganizationKind[] = ['owner', 'curator'];
export const kindToApi = (kind: OrganizationKind): string =>
  kind === 'curator' ? 'Curator' : 'Owner';
export const kindTerm = (kind: OrganizationKind) => vocab.ORGANIZATION_TYPES[kind];

export const plural = (n: number, one: string, many = `${one}s`) =>
  `${formatNumber(n)} ${n === 1 ? one : many}`;

export { cancelText, reinstateText } from '@/app/events/curateConfirm';

/** Under the form's date while a change would reach runners; nothing when nobody is interested. */
export const dateChangeText = (interest: number): string =>
  `The ${plural(interest, 'runner')} who marked interest will be told the new date.`;

/** What deleting takes with it, as far as the page knows. */
export function deleteText(raceCount: number | undefined, interest: number | undefined): string {
  const what =
    raceCount === undefined
      ? 'its races'
      : raceCount === 0
        ? null
        : raceCount === 1
          ? 'its race'
          : `its ${formatNumber(raceCount)} races`;
  const marked = interest ? `the interest ${plural(interest, 'runner')} marked` : null;
  const goes = [what, marked].filter(Boolean).join(' and ');
  return `It leaves the Runqo app at once${goes ? `, with ${goes}` : ''}. This can’t be undone.`;
}
